import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { FileInput, FileUploader, FileUploaderContent, FileUploaderItem } from '@/components/shadcn/file-uploader'
import { IconArrowDownOutline } from '@icons/IconArrowDownOutline'
import { IconImageOutline } from '@icons/IconImageOutline'
import { IconUploadOutline } from '@icons/IconUploadOutline'
import clsx from 'clsx'
import { useState } from 'react'
import { type Control, FieldPath, FieldValues } from 'react-hook-form'

interface ImageUploadFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  label: string
  maxSize?: number // in bytes, default 6MB
  acceptedFormats?: string[] // e.g., ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  placeholder?: string
  showPreview?: boolean
  showFileInfo?: boolean
  className?: string
  disabled?: boolean
  onFileChange?: (file: File | null) => void // Callback when file changes
}

/**
 * ImageUploadField - A reusable form field component for image uploads with drag-and-drop support
 * 
 * Features:
 * - Drag and drop file upload
 * - Image preview with automatic file reader
 * - File size validation
 * - File type validation
 * - Visual feedback for drag states
 * - File information display (name, size)
 * - Seamless React Hook Form integration
 * 
 * @example
 * ```tsx
 * <ImageUploadField
 *   control={form.control}
 *   name="image"
 *   label="Upload Profile Picture"
 *   maxSize={5 * 1024 * 1024} // 5MB
 *   acceptedFormats={['.jpg', '.png']}
 *   showPreview={true}
 *   onFileChange={(file) => console.log('File selected:', file?.name)}
 * />
 * ```
 */
export function ImageUploadField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  maxSize = 6 * 1024 * 1024, // Default 6MB
  acceptedFormats = ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  placeholder,
  showPreview = true,
  showFileInfo = true,
  className,
  disabled = false,
  onFileChange,
}: ImageUploadFieldProps<TFieldValues, TName>) {
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleFileSelect = (files: File[] | null, onChange: (file: File | null) => void) => {
    const file = files?.[0] || null
    onChange(file)

    // Call the optional callback
    onFileChange?.(file)

    // Create image preview if enabled
    if (file && showPreview) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    } else {
      setImagePreview(null)
    }
  }

  const maxSizeMB = maxSize / 1024 / 1024

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => {
        const currentFile = value as File | null | undefined
        const files = currentFile ? [currentFile] : []

        return (
          <FormItem className={className}>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <FileUploader
                value={files}
                onValueChange={(newFiles) => handleFileSelect(newFiles, onChange)}
                dropzoneOptions={{
                  accept: {
                    'image/*': acceptedFormats,
                  },
                  maxFiles: 1,
                  maxSize: maxSize,
                  multiple: false,
                  disabled: disabled,
                  onDragEnter: () => setIsDragging(true),
                  onDragLeave: () => setIsDragging(false),
                  onDrop: () => setIsDragging(false),
                }}
                className="relative space-y-1"
              >
                <FileInput
                  className={clsx('border-2 border-dashed border-gray-300 transition-colors', {
                    'border-solid border-primary bg-primary/5': isDragging,
                    hidden: files.length > 0 && imagePreview && showPreview,
                  })}
                >
                  <div className="flex w-full flex-col items-center justify-center gap-y-3 pb-8 pt-6">
                    <IconUploadOutline className={clsx('size-10 text-gray-400', { hidden: isDragging })} />
                    <IconArrowDownOutline className={clsx('size-10 text-primary', { hidden: !isDragging })} />
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700">
                        {isDragging
                          ? 'Drop image here'
                          : placeholder || 'Click to select or drag and drop'}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {acceptedFormats.map((f) => f.replace('.', '').toUpperCase()).join(', ')} up to {maxSizeMB}MB
                      </p>
                    </div>
                  </div>
                </FileInput>

                {/* Image Preview */}
                {showPreview && imagePreview && (
                  <div className="relative rounded-lg border-2 border-gray-200 bg-gray-50 p-4">
                    <div className="flex flex-col items-center gap-4">
                      <img src={imagePreview} alt="Preview" className="max-h-64 rounded-lg object-contain" />
                      {showFileInfo && (
                        <FileUploaderContent>
                          {files.map((file, i) => (
                            <FileUploaderItem key={i} index={i}>
                              <div className="flex items-center gap-x-2">
                                <IconImageOutline className="size-4" />
                                <span className="max-w-md truncate text-sm">{file.name}</span>
                                <span className="text-xs text-gray-500">
                                  ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                              </div>
                            </FileUploaderItem>
                          ))}
                        </FileUploaderContent>
                      )}
                    </div>
                  </div>
                )}

                {/* Fallback file display without preview */}
                {!showPreview && files.length > 0 && (
                  <FileUploaderContent>
                    {files.map((file, i) => (
                      <FileUploaderItem key={i} index={i}>
                        <div className="flex items-center gap-x-2">
                          <IconImageOutline className="size-4" />
                          <span className="max-w-md truncate text-sm">{file.name}</span>
                          {showFileInfo && (
                            <span className="text-xs text-gray-500">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          )}
                        </div>
                      </FileUploaderItem>
                    ))}
                  </FileUploaderContent>
                )}
              </FileUploader>
            </FormControl>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

